import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CampaignPaymentModal } from "@/components/CampaignPaymentModal";
import { toast } from "sonner";
import {
  Archive, Copy, Megaphone, Calendar, DollarSign, Clock, Loader2,
  Pencil, CreditCard, Trash2, RotateCcw, FileEdit, AlertTriangle,
} from "lucide-react";

/**
 * Where a campaign lives when it is not on the active list.
 *
 * Round 2 asked for two things that turn out to be the same page:
 *
 *   #36  "Draft campaigns should be captured under Archived in a drafts section, not
 *         left on the active campaigns page."
 *   #37  "Confirm an expired campaign moves to Archive in an expired section, with an
 *         expiry date and a CTA to restart it."
 *
 * Neither can physically MOVE into archived_campaigns, and that constraint shapes the
 * whole page:
 *
 *   - a draft must stay in db.campaigns to remain editable, and an unpaid draft is now
 *     DELETED rather than archived, so it never reaches that collection at all;
 *   - relaunch updates the live campaign document and refuses anything not EXPIRED, so
 *     archiving an expired campaign would break the very CTA #37 asks for.
 *
 * So this page SURFACES three groups from three sources rather than owning them. Drafts
 * and Expired are live campaigns filtered by status; only the third section is the
 * archive proper.
 */

const statusColors = {
  DRAFT: "bg-gray-500",
  ACTIVE: "bg-green-500",
  PAUSED: "bg-yellow-500",
  COMPLETED: "bg-blue-500",
  // EXPIRED was missing, so an expired campaign badged grey as though its status were
  // unrecognised - on the page whose job is to show expired campaigns.
  EXPIRED: "bg-red-500",
};

const DURATION_LABELS = {
  "1_month": "1 month",
  "3_months": "3 months",
  "6_months": "6 months",
  "12_months": "12 months",
};

// Bronze is 12-month only; Silver and Gold carry the full set. Falls back to the whole
// list rather than an empty one, so a tier we do not recognise still offers a choice
// instead of a dead dialog - the server validates the duration regardless.
// The relaunch dialog's term options. Round 11, SP-38's sibling: this was a hardcoded
// map, so a duration unticked in Admin > Tier Pricing still appeared here. It is now
// only the FALLBACK, used when /settings/tier-config has not answered yet.
const DURATIONS_BY_TIER = {
  BRONZE: ["12_months"],
};
const ALL_DURATIONS = ["1_month", "3_months", "6_months", "12_months"];

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-AU") : null;

export default function ArchivedCampaigns() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [campaigns, setCampaigns] = useState([]);
  const [archivedCampaigns, setArchivedCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [relaunchTarget, setRelaunchTarget] = useState(null);
  const [relaunchDuration, setRelaunchDuration] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [paymentModal, setPaymentModal] = useState({
    open: false, clientSecret: null, amount: 0, campaignName: "",
  });

  // Live tier config, so the relaunch terms match what an admin has configured.
  const [tierConfig, setTierConfig] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Both sources, one round trip each. Archived is allowed to fail on its own - a
      // merchant with drafts should still see them if the archive read breaks.
      const [live, archived, tiers] = await Promise.all([
        axios.get(`${API}/campaigns`, auth),
        axios.get(`${API}/campaigns/archived`, auth).catch(() => ({ data: [] })),
        // Round 11, SP-38's sibling. The relaunch dialog offered a hardcoded set of
        // terms, so a duration an admin unticked in Tier Pricing was still on sale
        // here. Allowed to fail on its own: the fallback map below is correct for
        // Bronze and permissive for the rest, which is the safe way round for a
        // dialog whose next step is a payment the backend validates anyway.
        axios.get(`${API}/settings/tier-config`, auth).catch(() => ({ data: null })),
      ]);
      setCampaigns(Array.isArray(live.data) ? live.data : live.data?.campaigns || []);
      setArchivedCampaigns(Array.isArray(archived.data) ? archived.data : []);
      if (tiers.data?.tiers) setTierConfig(tiers.data.tiers);
    } catch (error) {
      toast.error("Could not load campaigns");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const drafts = campaigns.filter(c => c.status === "DRAFT");
  const expired = campaigns.filter(c => c.status === "EXPIRED");

  // --- actions -------------------------------------------------------------------------

  const handleCopy = async (campaignId) => {
    setBusyId(campaignId);
    try {
      await axios.post(`${API}/campaigns/${campaignId}/copy`, {}, auth);
      toast.success("Copied to drafts");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not copy the campaign");
    } finally {
      setBusyId(null);
    }
  };

  const handleLaunch = async (campaign) => {
    setBusyId(campaign.id);
    try {
      const res = await axios.post(`${API}/campaigns/${campaign.id}/launch`, {}, auth);
      const { client_secret, breakdown } = res.data || {};
      if (!client_secret) {
        toast.success("Campaign is live");
        await load();
        return;
      }
      setPaymentModal({
        open: true,
        clientSecret: client_secret,
        amount: breakdown?.total ?? 0,
        breakdown,
        campaignName: campaign.name,
      });
    } catch (error) {
      // assert_launch_ready names the missing piece - "add a creative", not "failed".
      // Passing it through is the whole reason it says that.
      toast.error(error.response?.data?.detail || "Could not start the launch");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      // The server decides delete vs archive - it can see the ad ledger and we cannot -
      // so the confirmation reports its answer rather than our prediction.
      const res = await axios.delete(`${API}/campaigns/${deleteTarget.id}`, auth);
      const offersGone = res.data?.offers_deleted || 0;
      toast.success(
        res.data?.deleted
          ? `Draft deleted${offersGone ? ` (${offersGone} offer${offersGone === 1 ? "" : "s"} removed)` : ""}`
          : "Campaign archived"
      );
      setDeleteTarget(null);
      await load();
    } catch (error) {
      toast.error("Could not delete the draft");
    } finally {
      setBusyId(null);
    }
  };

  const openRelaunch = (campaign) => {
    const options = tierConfig?.[campaign.tier]?.allowed_durations
      || DURATIONS_BY_TIER[campaign.tier] || ALL_DURATIONS;
    setRelaunchTarget(campaign);
    // Preselect when there is only one lawful answer - Bronze has exactly one term, and
    // asking a merchant to choose from a list of one is a step for nothing.
    setRelaunchDuration(options.length === 1 ? options[0] : "");
  };

  const confirmRelaunch = async () => {
    if (!relaunchTarget || !relaunchDuration) return;
    setBusyId(relaunchTarget.id);
    try {
      // A new term is a new sale, not an edit: assert_repricing_allowed locks priced
      // fields once paid, so relaunch mints its own intent and the webhook promotes the
      // campaign when the money arrives.
      const res = await axios.post(
        `${API}/campaigns/${relaunchTarget.id}/relaunch?duration=${encodeURIComponent(relaunchDuration)}`,
        {}, auth
      );
      const { client_secret, tier_cost } = res.data || {};
      const name = relaunchTarget.name;
      setRelaunchTarget(null);
      if (!client_secret) {
        toast.success("Campaign relaunched");
        await load();
        return;
      }
      setPaymentModal({
        open: true, clientSecret: client_secret, amount: tier_cost || 0, campaignName: name,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not relaunch the campaign");
    } finally {
      setBusyId(null);
    }
  };

  // --- rendering -----------------------------------------------------------------------

  const StatusBadge = ({ status }) => (
    <Badge variant="outline" className="flex items-center gap-1">
      <span className={`w-2 h-2 rounded-full ${statusColors[status] || "bg-gray-500"}`} />
      {status || "Unknown"}
    </Badge>
  );

  const Row = ({ campaign, tint, badge, meta, actions }) => (
    <div
      className="flex flex-col gap-3 p-4 hover:bg-muted/30 transition-colors sm:flex-row sm:items-center sm:justify-between"
      data-testid={`campaign-row-${campaign.id}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={`p-2.5 rounded-lg ${tint} shrink-0`}>
          <Megaphone className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold truncate">{campaign.name}</h3>
            {badge}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {meta}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </div>
  );

  const Section = ({ title, blurb, count, children, testId }) => (
    <div data-testid={testId}>
      <div className="flex items-baseline gap-2 mb-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className="text-sm text-muted-foreground">{count}</span>
      </div>
      <p className="text-sm text-muted-foreground mb-3">{blurb}</p>
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="divide-y divide-border">{children}</div>
        </CardContent>
      </Card>
    </div>
  );

  const Empty = ({ children }) => (
    <p className="p-8 text-center text-sm text-muted-foreground">{children}</p>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading campaigns...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in max-w-[1400px]" data-testid="archived-campaigns-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-xl bg-amber-500/10">
            <Archive className="h-7 w-7 text-amber-500" />
          </div>
          <div>
            <h1 className="text-4xl font-black tracking-tight">Drafts &amp; Archive</h1>
            <p className="text-muted-foreground">
              Campaigns that are not live: unfinished drafts, campaigns whose term has
              ended, and your archive.
            </p>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------------- Drafts (#36) */}
      <Section
        testId="drafts-section"
        title="Drafts"
        count={drafts.length ? `${drafts.length}` : ""}
        blurb="Started but never launched. Nothing has been charged for these, and they are not being shown to anyone."
      >
        {drafts.length === 0 ? (
          <Empty>No drafts. Campaigns you start and save appear here until you launch them.</Empty>
        ) : drafts.map(campaign => (
          <Row
            key={campaign.id}
            campaign={campaign}
            tint="bg-muted text-muted-foreground"
            badge={
              <>
                <StatusBadge status="DRAFT" />
                {campaign.payment_status === "failed" && (
                  <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                    Payment declined
                  </Badge>
                )}
              </>
            }
            meta={
              <>
                {campaign.tier && <span>{campaign.tier}</span>}
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Saved {fmtDate(campaign.updated_at || campaign.created_at) || "recently"}
                </span>
              </>
            }
            actions={
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={() => navigate(`/campaigns?edit=${campaign.id}`)}
                  data-testid={`edit-draft-${campaign.id}`}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleLaunch(campaign)}
                  disabled={busyId === campaign.id}
                  data-testid={`launch-draft-${campaign.id}`}
                >
                  {busyId === campaign.id
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <CreditCard className="mr-2 h-4 w-4" />}
                  {campaign.payment_status === "failed" ? "Retry payment" : "Launch & pay"}
                </Button>
                <Button
                  variant="ghost" size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteTarget(campaign)}
                  data-testid={`delete-draft-${campaign.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            }
          />
        ))}
      </Section>

      {/* --------------------------------------------------------------- Expired (#37) */}
      <Section
        testId="expired-section"
        title="Expired"
        count={expired.length ? `${expired.length}` : ""}
        blurb="The term you paid for has ended, so these have stopped serving. Launch one again for a fresh term - its creatives, offers and targeting are kept."
      >
        {expired.length === 0 ? (
          <Empty>No expired campaigns.</Empty>
        ) : expired.map(campaign => (
          <Row
            key={campaign.id}
            campaign={campaign}
            tint="bg-red-500/10 text-red-500"
            badge={<StatusBadge status="EXPIRED" />}
            meta={
              <>
                {campaign.tier && <span>{campaign.tier}</span>}
                {/* The expiry date #37 asked for. end_at is what the sweep compared
                    against, so it is the date that actually stopped the campaign. */}
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Expired {fmtDate(campaign.end_at || campaign.end_date) || "date unknown"}
                </span>
                {campaign.start_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Ran from {fmtDate(campaign.start_at)}
                  </span>
                )}
              </>
            }
            actions={
              <Button
                size="sm"
                onClick={() => openRelaunch(campaign)}
                disabled={busyId === campaign.id}
                data-testid={`restart-campaign-${campaign.id}`}
              >
                {busyId === campaign.id
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <RotateCcw className="mr-2 h-4 w-4" />}
                {/* Round 11. Round 7 renamed the DIALOG to "Launch ... again" and
                    left the button that opens it saying "Restart", so the two
                    disagreed and the doc reported the rename as finished. */}
                Launch again
              </Button>
            }
          />
        ))}
      </Section>

      {/* ------------------------------------------------------------------- Archived */}
      <Section
        testId="archived-section"
        title="Archived"
        count={archivedCampaigns.length ? `${archivedCampaigns.length}` : ""}
        blurb="Kept for your records. An archived campaign cannot be brought back, but you can copy one into a new draft."
      >
        {archivedCampaigns.length === 0 ? (
          <Empty>Nothing archived yet.</Empty>
        ) : archivedCampaigns.map(campaign => (
          <Row
            key={campaign.id}
            campaign={campaign}
            tint="bg-amber-500/10 text-amber-500"
            badge={
              <>
                <StatusBadge status={campaign.original_status} />
                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                  <Archive className="h-3 w-3 mr-1" /> Archived
                </Badge>
              </>
            }
            meta={
              <>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {fmtDate(campaign.start_at || campaign.start_date) || "No date"}
                  {(campaign.end_at || campaign.end_date) &&
                    ` – ${fmtDate(campaign.end_at || campaign.end_date)}`}
                </span>
                {campaign.budget > 0 && (
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    ${campaign.budget?.toLocaleString()} budget
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Archived {fmtDate(campaign.archived_at) || "date unknown"}
                </span>
              </>
            }
            actions={
              <Button
                variant="outline" size="sm"
                onClick={() => handleCopy(campaign.id)}
                disabled={busyId === campaign.id}
                data-testid={`copy-campaign-${campaign.id}`}
              >
                {busyId === campaign.id
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Copying...</>
                  : <><Copy className="mr-2 h-4 w-4" /> Copy to drafts</>}
              </Button>
            }
          />
        ))}
      </Section>

      {/* Restart: pick a term, then pay for it. */}
      <Dialog open={!!relaunchTarget} onOpenChange={(open) => !open && setRelaunchTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-primary" />
              Launch {relaunchTarget?.name} again
            </DialogTitle>
            <DialogDescription className="pt-2">
              A new term is a new purchase - the one you bought has been used up. The
              campaign keeps its creatives, offers and targeting, and its {relaunchTarget?.tier} tier
              stays as it is.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">New term</label>
            <Select value={relaunchDuration} onValueChange={setRelaunchDuration}>
              <SelectTrigger data-testid="relaunch-duration">
                <SelectValue placeholder="Choose how long it runs" />
              </SelectTrigger>
              <SelectContent>
                {(tierConfig?.[relaunchTarget?.tier]?.allowed_durations
                  || DURATIONS_BY_TIER[relaunchTarget?.tier] || ALL_DURATIONS).map(d => (
                  <SelectItem key={d} value={d}>{DURATION_LABELS[d] || d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRelaunchTarget(null)}>Cancel</Button>
            <Button
              onClick={confirmRelaunch}
              disabled={!relaunchDuration || busyId === relaunchTarget?.id}
              data-testid="confirm-relaunch"
            >
              {busyId === relaunchTarget?.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Continue to payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deleting a draft is permanent, so the confirmation says so. */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete draft
            </DialogTitle>
            <DialogDescription className="pt-2">
              Delete <strong>&ldquo;{deleteTarget?.name}&rdquo;</strong> permanently?
              <br /><br />
              <span className="text-muted-foreground">
                This draft was never paid for, so there is nothing to keep. It and any
                offers on it are removed for good - this cannot be undone.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={busyId === deleteTarget?.id}
              data-testid="confirm-delete-draft"
            >
              {busyId === deleteTarget?.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CampaignPaymentModal
        open={paymentModal.open}
        onOpenChange={(open) => {
          setPaymentModal(p => ({ ...p, open }));
          // Refresh either way: an abandoned payment leaves the campaign where it was,
          // and the row should show that rather than a stale optimistic state.
          if (!open) load();
        }}
        clientSecret={paymentModal.clientSecret}
        amount={paymentModal.amount}
        breakdown={paymentModal.breakdown}
        campaignName={paymentModal.campaignName}
        token={token}
        onSuccess={() => {
          setPaymentModal({ open: false, clientSecret: null, amount: 0, campaignName: "" });
          toast.success("Payment received - your campaign is live");
          load();
        }}
      />
    </div>
  );
}
